/**
 * The layer-stack half of a run dialog, as pure functions.
 *
 * Same four-hook shape `stationVolumes/dialogModel.ts` established and both
 * existing run dialogs already spread: a zod fragment, a `superRefine` body,
 * a `defaultValues` fragment and a mutation-payload builder. Nothing here
 * touches React, jotai or i18n, so the dialog and its tests compute the same
 * answers and cannot disagree about what is blocking.
 *
 * Terminology, used exactly (global constraints): a **level** is a surface
 * (top of binder, formation); a **layer** is the material between two
 * adjacent levels. **N levels give N−1 layers.** Nothing in this module ever
 * asks how many layers there are — it only ever counts levels and derives.
 */
import { z } from 'zod';

import { PC_METHODS } from '@/components/SurfacePicker';
import type { LayerStackLevelInput, SurfaceInput } from '@/graphql/graphql';
import { layersOf } from '@/lib/layerStack/model';

/** Millimetres on screen, metres on the wire (global constraints). */
export const mmToM = (mm: number): number => mm / 1000;

/** A stack has to describe at least one layer, and one layer needs two levels. */
export const MIN_LEVELS = 2;
/**
 * Ceiling on the number of levels a single run may carry. Not a schema limit
 * — the worker runs `expectation.py` once per LAYER, and a stack byproduct
 * holds `2 * levelCount` section planes (see `estimateLayerStack`), so the
 * cost of a run is linear in this number. Twelve levels is eleven layers,
 * well past any real pavement build-up.
 */
export const MAX_LEVELS = 12;

export type PcMethod = (typeof PC_METHODS)[number];

/**
 * One LEVEL of the stack, in the field names `SurfacePicker` owns.
 *
 * `SurfacePicker` writes `${prefix}LayerId` / `${prefix}SurfaceIds` /
 * `${prefix}PcMethod`, and this form's prefixes are `levels.${i}.design` and
 * `levels.${i}.asBuilt` — so these names are not a style choice, they are the
 * contract with that component. Reusing it (rather than a bare `<Select>`) is
 * what keeps `pcMethod` and `surfaceIds` on the wire: a plain select drops
 * both silently.
 */
export interface LayerStackLevelForm {
  name: string;
  designLayerId: string;
  designSurfaceIds: string[];
  designPcMethod: PcMethod;
  /** Empty is PENDING — "not surveyed yet", never an error (global constraints). */
  asBuiltLayerId: string;
  asBuiltSurfaceIds: string[];
  asBuiltPcMethod: PcMethod;
  /** Millimetres, as typed. `null` means "measure this side, never fail on
   *  it"; `0` means "any excess at all is a defect". Never conflate them. */
  underTolMm: number | null;
  overTolMm: number | null;
  /**
   * Carries no value. An anchor path for the level-level issue that is about
   * the PAIR of pickers rather than either one ('levelSurfaceRequired'):
   * `SurfacePicker` renders an unconditional `<FormMessage/>` for
   * `${prefix}LayerId`, which prints the raw untranslated tag. Same trick,
   * and the same reason, as `expectSurfaces` in `surfaceExpectation.ts`.
   */
  levelSurfaces?: undefined;
}

export interface LayerStackFormValues {
  name: string;
  levels: LayerStackLevelForm[];
  resolution: number;
}

/** A level with no as-built surface has not been built yet. Absence is a
 *  state, never a zero, and never a validation failure. */
export const isLevelPending = (level: LayerStackLevelForm): boolean => !level.asBuiltLayerId;

/** Adjacent level pairs — the LAYERS. Re-exported from the shared model so
 *  the dialog and the result readers count layers the same single way. */
export { layersOf };

/**
 * Section-byproduct size for the live estimate line.
 *
 * A stack's `station_sections.f32` holds every level's AS-BUILT **and** every
 * level's DESIGN — `2 * levelCount` float32 planes over `(binCount + 1) *
 * nOffsets`, not the two planes a station-volumes run writes. The per-station
 * means (`station_offsets.f32`, three float32 planes = 12 bytes a cell) are
 * unchanged, which is exactly why a test must assert the PLANE term and not a
 * ratio on the total: the shared per-station term would mask the difference.
 *
 * Deliberately NOT a change to `stationVolumes/dialogModel.ts` — its `* 8` is
 * correct for its own 2-plane dialog.
 */
export function estimateLayerStack({
  binCount,
  nOffsets,
  levelCount,
}: {
  binCount: number;
  nOffsets: number;
  levelCount: number;
}): { sectionBytes: number; planeCount: number; levelCount: number; layerCount: number } {
  const levels = Math.max(0, Math.floor(levelCount));
  const planeCount = 2 * levels;
  return {
    levelCount: levels,
    layerCount: Math.max(0, levels - 1),
    planeCount,
    sectionBytes: binCount * nOffsets * 12 + (binCount + 1) * nOffsets * 4 * planeCount,
  };
}

/** One blank level. `nameFor` comes from the dialog so this module stays
 *  i18n-free (the same split `stationVolumesDefaults` uses for its alignment
 *  text). */
export const blankLevel = (index: number, nameFor: (i: number) => string): LayerStackLevelForm => ({
  name: nameFor(index),
  designLayerId: '',
  designSurfaceIds: [],
  designPcMethod: 'min',
  asBuiltLayerId: '',
  asBuiltSurfaceIds: [],
  asBuiltPcMethod: 'min',
  underTolMm: null,
  overTolMm: null,
  levelSurfaces: undefined,
});

/**
 * The index a NEW level should take its fallback name from: the first whose
 * name nobody is using.
 *
 * Naming from `levels.length` looks right and is not: remove level 1 of three
 * and the next Add mints a second "Level 3". `refineLayerStack` refuses
 * duplicate names, so Run goes dead — over a collision the user cannot see,
 * because the two rows carry the same word. Searching for a free name instead
 * keeps the numbering honest and is locale-independent (it compares against
 * the caller's own `nameFor`, whatever language that speaks).
 *
 * With `n` levels there are `n + 1` candidate names and at most `n` taken, so
 * the loop always finds one.
 */
export function nextLevelIndex(levels: { name: string }[], nameFor: (i: number) => string): number {
  const taken = new Set(levels.map((l) => l.name.trim()));
  for (let i = 0; i <= levels.length; i++) if (!taken.has(nameFor(i).trim())) return i;
  return levels.length;
}

/**
 * `defaultValues` for the stack half of the dialog's form.
 *
 * `levelCount` is a LEVEL count — the dialog never asks for a layer count, and
 * neither does this. `designLayerIdFor` seeds each level's design picker from
 * the candidate list where one exists (the same courtesy
 * `RunSurfaceDifferenceDialog` does for top/bottom); it returns `''` when
 * there is no obvious candidate, which leaves the level blank rather than
 * guessing.
 */
export function layerStackDefaults({
  levelCount = MIN_LEVELS,
  nameFor,
  designLayerIdFor = () => '',
  name = '',
  resolution = 0.25,
}: {
  levelCount?: number;
  nameFor: (i: number) => string;
  designLayerIdFor?: (i: number) => string;
  name?: string;
  resolution?: number;
}): LayerStackFormValues {
  const count = Math.max(0, Math.floor(levelCount));
  return {
    name,
    resolution,
    levels: Array.from({ length: count }, (_, i) => ({
      ...blankLevel(i, nameFor),
      designLayerId: designLayerIdFor(i),
    })),
  };
}

/**
 * Input and output types are kept IDENTICAL here — no `.default()`, no
 * `.optional()` on a field the form always carries. `useForm`'s resolver
 * generic splits into a separate transformed-values type the moment they
 * diverge, and every `<FormField control={…}>` in the dialog then fails to
 * type-check against it. `layerStackDefaults` supplies every field instead,
 * which is where a default belongs in this codebase anyway.
 */
const levelSchema = z.object({
  name: z.string(),
  designLayerId: z.string(),
  designSurfaceIds: z.array(z.string()),
  designPcMethod: z.enum(PC_METHODS),
  asBuiltLayerId: z.string(),
  asBuiltSurfaceIds: z.array(z.string()),
  asBuiltPcMethod: z.enum(PC_METHODS),
  underTolMm: z.number().nullable(),
  overTolMm: z.number().nullable(),
  // No value; an anchor for the pair-level issue. Do not "clean up" — without
  // it `ctx.addIssue({ path: ['levels', i, 'levelSurfaces'] })` targets a path
  // the schema does not know about.
  levelSurfaces: z.undefined().optional(),
});

/** The stack half of the dialog's zod object, to spread into `z.object({…})`. */
export const layerStackZodShape = {
  name: z.string(),
  levels: z.array(levelSchema),
  resolution: z.number(),
};

/**
 * The `superRefine` body: hard validity only.
 *
 * Issue `message`s are TAGS, not copy — the dialog maps them to translated
 * strings, the same way `refineStationVolumes` and `refineExpectation` do.
 * Everything advisory lives in `layerStackPreflight`, which never blocks.
 */
export function refineLayerStack(d: LayerStackFormValues, ctx: z.RefinementCtx): void {
  if (d.levels.length < MIN_LEVELS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['levels'], message: 'levelsRequired' });
  }
  if (d.levels.length > MAX_LEVELS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['levels'], message: 'tooManyLevels' });
  }
  if (!(d.resolution > 0)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['resolution'], message: 'resolutionPositive' });
  }

  const seen = new Map<string, number>();
  d.levels.forEach((level, i) => {
    const issue = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['levels', i, ...path], message });

    const trimmed = level.name.trim();
    if (!trimmed) issue(['name'], 'levelNameRequired');
    else if (seen.has(trimmed.toLowerCase())) issue(['name'], 'duplicateLevelName');
    else seen.set(trimmed.toLowerCase(), i);

    // Neither surface means the level describes nothing at all. Note this is
    // NOT the PENDING case: a level with a design and no as-built is a normal,
    // valid, not-yet-built level.
    if (!level.designLayerId && !level.asBuiltLayerId) issue(['levelSurfaces'], 'levelSurfaceRequired');

    for (const k of ['underTolMm', 'overTolMm'] as const) {
      const v = level[k];
      if (typeof v === 'number' && v < 0) issue([k], 'toleranceNegative');
    }
  });
}

export interface LayerStackDerived {
  levelCount: number;
  /** N−1, always derived, never asked for. */
  layerCount: number;
  /** Adjacent index pairs, one per layer. */
  layerPairs: [number, number][];
  tooFewLevels: boolean;
  tooManyLevels: boolean;
  duplicateNameIndices: number[];
  missingNameIndices: number[];
  noSurfaceIndices: number[];
  negativeToleranceIndices: number[];
  /** Levels with no as-built surface: not yet built (PENDING), not invalid. */
  pendingIndices: number[];
  resolutionInvalid: boolean;
  /** True when Run must stay disabled. PENDING levels are deliberately absent. */
  blocked: boolean;
}

/** Everything the fields render and the Run button reads, in one pure
 *  function, so the two can never disagree about what is blocking. */
export function deriveLayerStack(values: LayerStackFormValues): LayerStackDerived {
  const levels = values.levels;
  const seen = new Map<string, number>();
  const duplicateNameIndices: number[] = [];
  const missingNameIndices: number[] = [];
  levels.forEach((l, i) => {
    const key = l.name.trim().toLowerCase();
    if (!key) missingNameIndices.push(i);
    else if (seen.has(key)) duplicateNameIndices.push(i);
    else seen.set(key, i);
  });

  const noSurfaceIndices = levels.flatMap((l, i) => (!l.designLayerId && !l.asBuiltLayerId ? [i] : []));
  const negativeToleranceIndices = levels.flatMap((l, i) =>
    (typeof l.underTolMm === 'number' && l.underTolMm < 0) || (typeof l.overTolMm === 'number' && l.overTolMm < 0)
      ? [i]
      : [],
  );
  const pendingIndices = levels.flatMap((l, i) => (isLevelPending(l) ? [i] : []));

  const tooFewLevels = levels.length < MIN_LEVELS;
  const tooManyLevels = levels.length > MAX_LEVELS;
  const resolutionInvalid = !(values.resolution > 0);

  return {
    levelCount: levels.length,
    layerCount: Math.max(0, levels.length - 1),
    layerPairs: layersOf(levels),
    tooFewLevels,
    tooManyLevels,
    duplicateNameIndices,
    missingNameIndices,
    noSurfaceIndices,
    negativeToleranceIndices,
    pendingIndices,
    resolutionInvalid,
    blocked:
      tooFewLevels ||
      tooManyLevels ||
      resolutionInvalid ||
      duplicateNameIndices.length > 0 ||
      missingNameIndices.length > 0 ||
      noSurfaceIndices.length > 0 ||
      negativeToleranceIndices.length > 0,
  };
}

const surfaceInput = (layerId: string, surfaceIds: string[], pcMethod: PcMethod): SurfaceInput | undefined =>
  layerId ? { layerId, surfaceIds: surfaceIds.length ? surfaceIds : undefined, pcMethod } : undefined;

/**
 * The mutation payload. **The only place millimetres become metres.**
 *
 * The caller must have already checked the feature gate — it can lapse while
 * the dialog is open and the mutation costs real compute, so the last word
 * belongs to the call site, not to a stale form flag (same contract as
 * `stationVolumesPayload`).
 *
 * A blank tolerance stays `null`, never 0: `null` means "measure this side,
 * never fail on it", `0` means "any excess at all is a defect".
 */
export function layerStackPayload(values: LayerStackFormValues): {
  name?: string;
  levels: LayerStackLevelInput[];
  params: { resolution: number };
} {
  const mm = (v: number | null | undefined) => (v == null ? null : mmToM(v));
  return {
    name: values.name.trim() || undefined,
    levels: values.levels.map((l) => ({
      name: l.name.trim(),
      design: surfaceInput(l.designLayerId, l.designSurfaceIds, l.designPcMethod),
      asBuilt: surfaceInput(l.asBuiltLayerId, l.asBuiltSurfaceIds, l.asBuiltPcMethod),
      underToleranceM: mm(l.underTolMm),
      overToleranceM: mm(l.overTolMm),
    })),
    params: { resolution: values.resolution },
  };
}

/** Advisory only — never a blocker. `level` is the literal `'warn'`, so "this
 *  never blocks" is a compile-time guarantee at every call site (the shape
 *  `ExpectationWarning` established, and for the same reason). */
export type LayerStackWarningKey = 'toleranceTooFine' | 'layersWithoutDesign';

export interface LayerStackWarning {
  level: 'warn';
  key: LayerStackWarningKey;
  params?: Record<string, unknown>;
}

const round = (v: number, dp: number): number => Math.round(v * 10 ** dp) / 10 ** dp;

/**
 * Pre-flight advice for the stack, in the same place the expectation's lives
 * and with the same rule: it never blocks.
 *
 * Two things worth saying and never worth refusing a run over:
 *  - a tolerance finer than a tenth of a grid cell measures grid noise;
 *  - a layer whose two levels have no design surfaces between them has no
 *    design thickness to check against, so it can only ever report as-built
 *    geometry.
 */
export function layerStackPreflight(values: LayerStackFormValues): LayerStackWarning[] {
  const warnings: LayerStackWarning[] = [];
  const res = values.resolution;
  if (typeof res === 'number' && res > 0) {
    const floorMm = (res * 1000) / 10;
    const breaching = values.levels
      .flatMap((l) => [l.underTolMm, l.overTolMm])
      .filter((t): t is number => typeof t === 'number' && t >= 0 && t < floorMm);
    if (breaching.length > 0) {
      warnings.push({
        level: 'warn',
        key: 'toleranceTooFine',
        params: { mm: round(Math.min(...breaching), 1), res: round(res, 3) },
      });
    }
  }

  const noDesign = layersOf(values.levels).filter(
    ([a, b]) => !values.levels[a].designLayerId || !values.levels[b].designLayerId,
  );
  if (noDesign.length > 0) {
    warnings.push({ level: 'warn', key: 'layersWithoutDesign', params: { count: noDesign.length } });
  }

  return warnings;
}
